/**
 * @fileoverview Core base types for the resource system.
 * 
 * This module defines the fundamental building blocks that all resources build upon.
 * All resource types (tools, guides, glossary terms, etc.) extend from these base types
 * to ensure consistency and shared functionality across the system.
 * 
 * The module provides a unified way to represent all resource types under a single,
 * consistent taxonomy. It addresses TypeScript's discriminated union challenges when
 * working with heterogeneous collections of resources.
 * 
 * @module resources/types/shared/base
 * 
 */

import type { ResourceType, ToolType, ToolCategory, DifficultyLevel, LearningResourceType, GlossaryCategory } from '~/lib/resources/types/shared/enums'
import type { BaseToolConfig } from '~/lib/resources/types/shared/configs'
import type { RelatedTerm, Example, GlossaryTerm } from '~/lib/resources/types/glossary/terms'
import type { BaseContentSection } from '~/lib/resources/types/shared/sections'
import type { Guide, ContentGuide, SpecsGuide } from '~/lib/resources/types/guides'
import type { Template } from '~/lib/resources/types/templates'
import type { CaseStudy } from '~/lib/resources/types/case-studies'
import type { Ebook } from '~/lib/resources/types/ebooks'
import type { Calculator } from '~/lib/resources/types/tools/calculator'
import type { Quiz } from '~/lib/resources/types/tools/quiz'
import type { Generator } from '~/lib/resources/types/tools/generator'
import type { Analyzer } from '~/lib/resources/types/tools/analyzer'
import { type ReactNode } from 'react'

/**
 * Represents a unified reference to any learning resource, whether internal or external.
 * This interface consolidates and replaces:
 * 1. The ResourceRecommendation type used in quizzes for learning suggestions
 * 2. The ExternalResource type used in glossary terms for reference links
 * 
 * @interface SupplementalResourceReference
 * @property {LearningResourceType} learningResourceType - The type of learning resource (e.g. 'course', 'tool', 'article')
 * @property {boolean} isInternal - Whether this is an internal resource within our system
 * @property {ResourceType} [resourceType] - For internal resources: the specific resource type classification
 * @property {ToolType} [toolType] - For internal tool resources: the specific tool type
 * @property {GlossaryCategory} [glossaryCategory] - For internal glossary terms: the glossary category
 * @property {string} [category] - Category for optional grouping and filtering resources
 * @property {string} [title] - Title/name of the resource
 * @property {string} [url] - Complete URL for external resources or full path for internal ones
 * @property {string} [baseUrl] - Base domain URL - only used for external resources
 * @property {string} [urlSlug] - The entire slug/path after the baseUrl
 * @property {string} [resourceSlug] - What we refer to as "slug" in this codebase, being the FINAL path segment
 * @property {string} [slug] - Kept for compatibility but should not generally be used
 * @property {string} [id] - Unique identifier for the resource
 * @property {string} [description] - Detailed description of the resource content
 * @property {string} [text] - Additional context about why this resource is relevant
 * @property {DifficultyLevel} [difficulty] - Indicates the complexity/expertise level required
 * @property {string[]} [tags] - Categorical labels for filtering and organization
 * @property {string} [blogSlug] - Specific slug segment (FINAL path segment) for blog articles
 * @property {string} [blogTopic] - Topic category for blog articles
 * 
 * @example Internal resource reference
 * ```ts
 * const internalRef: SupplementalResourceReference = {
 *   learningResourceType: 'tool',
 *   isInternal: true,
 *   resourceType: 'tool',
 *   toolType: 'quiz',
 *   title: 'Media Buyer Assessment',
 *   urlSlug: '/resources/tools/quizzes/media-buyer-skills-assessment',
 *   resourceSlug: 'media-buyer-skills-assessment'
 * }
 * ```
 * 
 * @example External resource reference
 * ```ts
 * const externalRef: SupplementalResourceReference = {
 *   learningResourceType: 'course',
 *   isInternal: false,
 *   title: 'Meta Blueprint Certification',
 *   baseUrl: 'https://www.facebookblueprint.com',
 *   urlSlug: '/',
 *   difficulty: 'beginner'
 * }
 * ```
 */
export interface SupplementalResourceReference {
  /** The type of learning resource (e.g. 'course', 'tool', 'article', etc.) */
  learningResourceType: LearningResourceType
  /** Whether this is an internal resource within our system */
  isInternal: boolean
  /** For internal resources: the specific resource type classification */
  resourceType?: ResourceType
  /** For internal tool resources: the specific tool type */
  toolType?: ToolType
  /** For internal glossary terms: the glossary category */
  glossaryCategory?: GlossaryCategory
  /** Category for optional grouping and filtering resources */
  category?: string
  /** Title/name of the resource */
  title?: string
  /** Complete URL for external resources or full path for internal ones */
  url?: string
  /** Base domain URL - only used for external resources */
  baseUrl?: string
  /** The entire slug/path after the baseUrl */
  urlSlug?: string
  /** What we refer to as "slug" in this codebase, being the FINAL path segment of the URL */
  resourceSlug?: string
  /** Kept for compatibility but should not generally be used */
  slug?: string
  /** Unique identifier for the resource */
  id?: string
  /** Detailed description of the resource content */
  description?: string
  /** Additional context about why this resource is relevant or recommended */
  text?: string
  /** Indicates the complexity/expertise level required */
  difficulty?: DifficultyLevel
  /** Categorical labels for filtering and organization */
  tags?: string[]
  /** Specific slug for blog articles */
  blogSlug?: string
  /** Topic category for blog articles */
  blogTopic?: string
}

/**
 * Represents a reference to another resource in the system.
 * Used to establish relationships between resources while maintaining type safety
 * and providing enough information to locate the referenced resource.
 * 
 * @interface RelatedResource
 * @property {string} id - The unique identifier of the related resource
 * @property {ResourceType} resourceType - The type of the related resource
 * @property {ToolType} [toolType] - The type of the related tool (if it is a tool)
 * @property {GlossaryCategory} [glossaryCategory] - The category of the related glossary term (if it is a glossary term)
 * @property {string} title - The title of the related resource
 * @property {string} slug - The slug/end of the path of the related resource
 * @property {string} [fullRoute] - The full route of the related resource
 * @property {string} [relationshipDescription] - Optional description of how/why resources are related
 * @property {number} [relevanceScore] - Optional strength/relevance score (0-1) of the relationship
 * @property {Object} [metadata] - Optional metadata specific to the relationship
 * @property {boolean} [metadata.isBidirectional] - Whether this is a bidirectional relationship
 * @property {string} [metadata.relationType] - Custom relationship type (e.g. "prerequisite")
 */
export interface RelatedResource {
  /** The unique identifier of the related resource */
  id: string
  /** The type of the related resource */
  resourceType: ResourceType
  /** The type of the related tool (if it is a tool) */
  toolType?: ToolType
  /** The category of the related glossary term (if it is a glossary term) */
  glossaryCategory?: GlossaryCategory
  /** The title of the related resource */
  title: string
  /** The slug/end of the path of the related resource */
  slug: string
  /** The full route of the related resource */
  fullRoute?: string
  /** Optional description of how/why resources are related */
  relationshipDescription?: string
  /** Optional strength/relevance score (0-1) of the relationship */
  relevanceScore?: number
  /** Optional metadata specific to the relationship */
  metadata?: {
    /** Whether this is a bidirectional relationship */
    isBidirectional?: boolean
    /** Custom relationship type (e.g. "prerequisite", "alternative", "complement") */
    relationType?: string
  }
}

// =========================================
// Shared Resource Properties
// =========================================

/**
 * Base properties that are common across ALL resources.
 * 
 * @interface BaseResourceProperties
 * @property {string} id - Unique identifier for the resource
 * @property {string} title - Display title shown to users
 * @property {string} [canonicalTitle] - Canonical title for URL/SEO purposes, used when the display title isn't optimal for URLs
 * @property {string} description - Comprehensive resource description
 * @property {string} slug - URL-friendly identifier used in routing
 * @property {string[]} [alternativeSlugs] - Alternative URL-friendly identifiers for redirects or legacy support
 * @property {string} [category] - Primary categorization of the resource
 * @property {string[]} [tags] - Array of tags for filtering and organization
 * @property {ResourceStats} [stats] - Usage and engagement statistics
 * @property {ResourceImages} [images] - Associated images for various display contexts
 * @property {MetadataConfig} [metadata] - SEO and sharing metadata
 * @property {boolean} [featured] - Whether this is a featured/highlighted resource
 * @property {(string[] | RelatedResource[])} [relatedResources] - Related resources by slug/ID or as full resource references
 * @property {string} [estimatedTime] - Estimated time to consume or complete the resource
 * @property {DifficultyLevel} [difficulty] - Difficulty level of the resource
 * @property {string} [lastUpdated] - ISO timestamp of last significant update
 * @property {FAQItem[]} [faqs] - Frequently asked questions related to this resource
 * 
 * @see {@link ResourceStats} - For details on usage statistics structure
 * @see {@link ResourceImages} - For details on image structure
 * @see {@link MetadataConfig} - For SEO metadata configuration
 * @see {@link RelatedResource} - For related resource reference structure
 * @see {@link FAQItem} - For FAQ item structure
 */
export interface BaseResourceProperties {
  /** Unique identifier */
  id: string
  /** Display title */
  title: string
  /** Canonical title for URL/SEO purposes */
  canonicalTitle?: string
  /** Resource description */
  description: string
  /** URL-friendly identifier */
  slug: string
  /** Alternative slugs */
  alternativeSlugs?: string[]
  /** Resource category */
  category?: string
  /** Resource tags */
  tags?: string[]
  /** Usage statistics */
  stats?: ResourceStats
  /** Resource images */
  images?: ResourceImages
  /** Optional metadata for SEO */
  metadata?: MetadataConfig
  /** Whether this is a featured resource */
  featured?: boolean
  /** Related resources by slug/ID */
  relatedResources?: string[] | RelatedResource[]
  /** Estimated time to complete */
  estimatedTime?: string
  /** Difficulty level */
  difficulty?: DifficultyLevel
  /** Timestamp of last update */
  lastUpdated?: string
  /** Frequently asked questions */
  faqs?: FAQItem[]
  /**
   * Optional `noindex` hint propagated through `generateResourceMetadata`
   * into the robots meta tag. Defaults to indexable (omit the field).
   *
   * Primary use: 1-line rollback for landing-variant pages that begin
   * cannibalising their umbrella generator (see `landingVariants.ts`
   * — set `noindex: true` on the offender, redeploy, wait one crawl
   * cycle; Google drops the URL from the index without code churn or
   * a 301 chain).
   */
  noindex?: boolean
}

/**
 * Resource images interface.
 * 
 * @interface ResourceImages
 * @property {ResourceImage} hero - Main hero/banner image for resource detail pages
 * @property {ResourceImage} thumbnail - Smaller image for listings and cards
 * @property {ResourceImage} og - Open Graph image optimized for social media sharing
 * @property {ResourceImage} [icon] - Optional icon representation of the resource
 * 
 * @see {@link ResourceImage} - For details on image structure with metadata
 */
export interface ResourceImages {
  /** Main hero image URL */
  hero: ResourceImage
  /** Thumbnail image URL */
  thumbnail: ResourceImage
  /** Open Graph image URL for social sharing */
  og: ResourceImage
  /** Optional icon image URL */
  icon?: ResourceImage
}

/**
 * Resource image with metadata.
 * Similar to BlogImage but specialized for resources.
 * 
 * @interface ResourceImage
 * @property {string} url - Image URL
 * @property {number} [width] - Image width in pixels
 * @property {number} [height] - Image height in pixels
 * @property {string} [alt] - Alt text for accessibility
 * @property {string} [caption] - Optional caption text
 */
export interface ResourceImage {
  /** Image URL */
  url: string
  /** Image width in pixels */
  width?: number
  /** Image height in pixels */
  height?: number
  /** Alt text for accessibility */
  alt?: string
  /** Optional caption text */
  caption?: string
}

/**
 * Resource statistics interface.
 * 
 * @interface ResourceStats
 * @property {number} [views] - Number of resource views
 * @property {number} [shares] - Number of times shared
 * @property {number} [downloads] - Number of downloads if applicable
 * @property {number} [completions] - Number of times completed
 * @property {number} [averageRating] - Average rating
 * @property {number} [totalRatings] - Total number of ratings
 * @property {string} [lastUpdated] - Last updated timestamp
 */
export interface ResourceStats {
  /** Number of resource views */
  views?: number
  /** Number of times shared */
  shares?: number
  /** Number of downloads if applicable */
  downloads?: number
  /** Number of times completed */
  completions?: number
  /** Average rating */
  averageRating?: number
  /** Total number of ratings */
  totalRatings?: number
  /** Last updated timestamp */
  lastUpdated?: string
}

/**
 * Resource metadata configuration.
 * 
 * @interface MetadataConfig
 * @property {string} [title] - SEO title override (if different from resource title)
 * @property {string} [description] - SEO meta description (typically 150-160 characters)
 * @property {string[]} [keywords] - SEO keywords for search engine indexing
 * @property {string} [ogImage] - Custom Open Graph image URL override
 * @property {string} [canonicalURL] - Canonical URL if different from default resource URL
 * @property {string} [author] - Content author's name
 * @property {string} [authorUrl] - URL to author's profile or page
 * @property {string} [dateCreated] - ISO date when resource was first created
 * @property {string} [dateModified] - ISO date when resource was last modified
 * @property {string[]} [tags] - Additional tags specifically for SEO purposes
 */
export interface MetadataConfig {
  /** SEO title override */
  title?: string
  /** SEO description */
  description?: string
  /** SEO keywords */
  keywords?: string[]
  /** Custom OG image override */
  ogImage?: string
  /** Canonical URL if different from default */
  canonicalURL?: string
  /** Author name */
  author?: string
  /** Author URL */
  authorUrl?: string
  /** Date resource was created */
  dateCreated?: string
  /** Date resource was last modified */
  dateModified?: string
  /** Resource tags */
  tags?: string[]
}

// =========================================
// Base Resource Types
// =========================================

/**
 * Base Resource type that enforces strict resource type checking.
 * All concrete resource implementations must extend this interface.
 * 
 * @interface BaseResource
 * @extends BaseResourceProperties
 * @property {ResourceType} resourceType - The specific type of resource (e.g., 'guide', 'tool')
 * 
 * @see {@link ResourceType} - For all possible resource types
 */
export interface BaseResource extends BaseResourceProperties {
  /** The specific type of resource */
  resourceType: ResourceType
}

// =========================================
// Resource Type System
// =========================================

/**
 * Resource type mapping to their specific implementations.
 * This ensures type safety across the resource system by mapping
 * each resource type string to its concrete implementation interface.
 * 
 * @typedef {Object} ResourceTypeMap
 * 
 * @see {@link Guide} - For guide resource structure
 * @see {@link ContentGuide} - For content guide structure
 * @see {@link SpecsGuide} - For specifications guide structure
 * @see {@link Calculator} - For calculator tool structure
 * @see {@link Quiz} - For quiz tool structure
 * @see {@link Generator} - For generator tool structure
 * @see {@link Analyzer} - For analyzer tool structure
 * @see {@link Template} - For template resource structure
 * @see {@link CaseStudy} - For case study resource structure
 * @see {@link Ebook} - For ebook resource structure
 * @see {@link GlossaryTerm} - For glossary term structure
 */
export type ResourceTypeMap = {
  'guide': Guide | ContentGuide | SpecsGuide
  'tool': Calculator | Quiz | Generator | Analyzer
  'template': Template
  'case-study': CaseStudy
  'ebook': Ebook
  'glossary-term': GlossaryTerm
}

/**
 * Union type of all possible resource implementations.
 * This type represents any valid resource in the system.
 * 
 * @typedef {ResourceTypeMap[keyof ResourceTypeMap]} ResourceInstance
 */
export type ResourceInstance = ResourceTypeMap[keyof ResourceTypeMap]

/**
 * Type guard to check if a value is a valid resource instance.
 * Used for runtime type checking of resource objects.
 * 
 * @param {unknown} value - The value to check
 * @returns {boolean} True if value is a valid resource instance
 */
export function isResourceInstance(value: unknown): value is ResourceInstance {
  if (typeof value !== 'object' || value === null) return false
  const resource = value as { resourceType?: unknown }
  const resourceType = resource.resourceType
  return typeof resourceType === 'string' && 
         (resourceType === 'guide' ||
          resourceType === 'tool' ||
          resourceType === 'template' ||
          resourceType === 'case-study' ||
          resourceType === 'ebook' ||
          resourceType === 'glossary-term')
}

// =========================================
// Base Tool Types
// =========================================

/**
 * Base properties for all tools.
 * 
 * @interface BaseToolProperties
 * @extends BaseResourceProperties
 * @property {ToolCategory} toolCategory - Tool category for organization and filtering
 * @property {string[]} [relatedTools] - Related tools for cross-referencing
 * 
 * @see {@link ToolCategory} - For possible tool categories
 */
export interface BaseToolProperties extends BaseResourceProperties {
  /** Tool category for organization */
  toolCategory: ToolCategory
  /** Related tools by ID */
  relatedTools?: string[]
}

/**
 * Base tool type that all specific tools extend from.
 * 
 * @interface BaseTool
 * @template TConfig - The type of configuration this tool uses
 * @extends BaseToolProperties
 * @property {'tool'} resourceType - Resource type identifier, always 'tool'
 * @property {ToolType} toolType - Specific type of tool (e.g., 'calculator', 'quiz')
 * @property {TConfig} config - Tool-specific configuration
 * 
 * @see {@link ToolType} - For possible tool types
 * @see {@link BaseToolConfig} - For base tool configuration structure
 */
export interface BaseTool<TConfig extends BaseToolConfig = BaseToolConfig> extends BaseToolProperties {
  /** Resource type identifier, always 'tool' */
  resourceType: 'tool'
  /** Specific type of tool */
  toolType: ToolType
  /** Tool-specific configuration */
  config: TConfig
}

/**
 * Tool type that combines all possible tool implementations.
 * Union type representing any valid tool in the system.
 * 
 * @typedef {Calculator | Quiz | Generator | Analyzer} Tool
 * 
 * @see {@link Calculator} - For calculator tool structure
 * @see {@link Quiz} - For quiz tool structure
 * @see {@link Generator} - For generator tool structure
 * @see {@link Analyzer} - For analyzer tool structure
 */
export type Tool = Calculator | Quiz | Generator | Analyzer

// =========================================
// Base Glossary Types
// =========================================

/**
 * Common properties shared by all glossary terms.
 * Glossary terms provide definitions and context for industry terminology.
 * 
 * @typedef {BaseResourceProperties & {
 *   resourceType: 'glossary-term',
 *   shortDescription: string,
 *   definition: string,
 *   acronym?: string,
 *   relatedTerms?: RelatedTerm[],
 *   supplementalResources?: SupplementalResourceReference[],
 *   additionalContent?: BaseContentSection[],
 *   keyPoints?: string[],
 *   examples?: Example[],
 *   bestPractices?: string[],
 *   bestUsedFor?: string[],
 *   platforms?: Array<{name: string, description: string}>,
 *   lastUpdated: string
 * }} BaseGlossaryTerm
 * 
 * @property {string} resourceType - Resource type identifier, always 'glossary-term'
 * @property {string} shortDescription - Short description for previews and listings (typically 1-2 sentences)
 * @property {string} definition - Comprehensive definition of the term
 * @property {string} [acronym] - Acronym or abbreviation if applicable
 * @property {RelatedTerm[]} [relatedTerms] - Related terminology with explanations of relationships
 * @property {SupplementalResourceReference[]} [supplementalResources] - Additional resources and references for further reading
 * @property {BaseContentSection[]} [additionalContent] - Additional structured content sections
 * @property {string[]} [keyPoints] - Key points or takeaways about the term
 * @property {Example[]} [examples] - Practical examples of the term in use
 * @property {string[]} [bestPractices] - Recommended practices when using this term/concept
 * @property {string[]} [bestUsedFor] - Scenarios where this term/concept is most applicable
 * @property {Array<{name: string, description: string}>} [platforms] - Platforms or environments where this term/feature is available
 * @property {string} lastUpdated - ISO timestamp of last significant update (required for glossary terms)
 * 
 * @see {@link RelatedTerm} - For related term structure
 * @see {@link SupplementalResourceReference} - For supplemental resource structure
 * @see {@link BaseContentSection} - For content section structure
 * @see {@link Example} - For example structure
 */
export type BaseGlossaryTerm = BaseResourceProperties & {
  resourceType: 'glossary-term'
  /** Short description for previews */
  shortDescription: string
  /** Full definition */
  definition: string
  /** Acronym if applicable */
  acronym?: string
  /**
   * Alternate names and common variants/misspellings for the term.
   *
   * Used to populate `alternateName` on the emitted `DefinedTerm`
   * structured data so search engines and AI assistants can resolve
   * queries that don't use the canonical spelling — e.g. "thurplay",
   * "thrueplay", "thuplay", "thumb stop rate" all resolving to the
   * ThruPlay Rate / Thumbstop Rate definitions. Populate with: (a)
   * spelling variants and well-known misspellings we observe in
   * Search Console, (b) plain-language phrasings ("view-through
   * rate" alongside "VTR"), and (c) British/American spellings.
   * Do NOT include the canonical title or `acronym` — those are
   * already emitted as `name` and `alternateName`.
   */
  alternateNames?: string[]
  /** Related terms */
  relatedTerms?: RelatedTerm[]
  /** Supplemental resources and references */
  supplementalResources?: SupplementalResourceReference[]
  /** Additional content sections */
  additionalContent?: BaseContentSection[]
  /** Key points about the term */
  keyPoints?: string[]
  /** Examples of the term in use */
  examples?: Example[]
  /** Best practices */
  bestPractices?: string[]
  /** Best use cases */
  bestUsedFor?: string[]
  /** Platforms where this term/feature is available */
  platforms?: Array<{
    name: string
    description: string
  }>
  /** Last modification timestamp */
  lastUpdated: string
} 

/**
 * Represents a single FAQ item
 * 
 * @interface FAQItem
 * @property {string} question - The question text
 * @property {ReactNode} answer - The answer content - can be text or React nodes for rich formatting
 * @property {string} [category] - Optional category for grouping related FAQs
 * @property {ReactNode} [icon] - Optional icon to display next to the question
 */
export interface FAQItem {
  /** The question text */
  question: string
  /** The answer content - can be text or React nodes */
  answer: ReactNode
  /** Optional category for grouping FAQs */
  category?: string
  /** Optional icon to display next to the question */
  icon?: ReactNode
}